/**
 * Flow Executor - Frontend flow execution service
 * 
 * Note: Most flow execution now happens on the backend (whatsapp-server.js)
 * This service is kept for frontend preview/testing purposes only.
 */

import { Node, Edge } from 'reactflow';
import dbService from './dbService';
import whatsappService from './whatsappService';

interface ExecutionContext {
  flowId: string;
  flowName: string;
  userId?: string;
  message?: string;
  variables: Record<string, any>;
  startTime: number;
  nodesExecuted: number;
}

class FlowExecutor {
  private isExecuting = false;

  /**
   * Execute a flow (for testing/preview purposes)
   * Real execution happens on the backend
   */
  async executeFlow(
    flowId: string,
    nodes: Node[],
    edges: Edge[],
    trigger: { type: string; data: any }
  ): Promise<boolean> {
    if (this.isExecuting) {
      console.log('Another flow is already executing');
      return false;
    }

    this.isExecuting = true;
    const flow = dbService.getFlow(flowId);

    const context: ExecutionContext = {
      flowId,
      flowName: flow?.name || flowId,
      userId: trigger.data.userId,
      message: trigger.data.message,
      variables: {
        ...trigger.data,
        timestamp: new Date().toISOString()
      },
      startTime: Date.now(),
      nodesExecuted: 0
    };

    try {
      dbService.createLog({
        flowId,
        level: 'info',
        message: `Flow "${context.flowName}" execution started`,
        details: { trigger: trigger.type, userId: context.userId }
      });

      const triggerNode = nodes.find(n => n.type === 'trigger');
      if (!triggerNode) {
        throw new Error('No trigger node found in flow');
      }

      await this.executeNode(triggerNode, nodes, edges, context);

      const duration = Date.now() - context.startTime;
      dbService.createLog({
        flowId,
        level: 'info',
        message: `Flow "${context.flowName}" completed successfully`,
        details: { duration: `${duration}ms`, nodesExecuted: context.nodesExecuted }
      });

      dbService.recordFlowExecution(flowId, true, duration);
      return true;
    } catch (error) {
      dbService.createLog({
        flowId,
        level: 'error',
        message: `Error executing flow "${context.flowName}"`,
        details: { error: (error as Error).message }
      });
      return false;
    } finally {
      this.isExecuting = false;
    }
  }

  private async executeNode(
    node: Node,
    nodes: Node[],
    edges: Edge[],
    context: ExecutionContext
  ): Promise<void> {
    context.nodesExecuted++;
    console.log(`Executing node: ${node.type} - ${node.data.label}`);

    let nextNodeId: string | null = null;

    switch (node.type) {
      case 'trigger':
        nextNodeId = this.getNextNodeId(node.id, edges);
        break;

      case 'action':
        await this.executeActionNode(node.data, context);
        nextNodeId = this.getNextNodeId(node.id, edges);
        break;

      case 'condition':
        nextNodeId = this.executeConditionNode(node, node.data, edges, context);
        break;

      case 'delay':
        await this.executeDelayNode(node.data);
        nextNodeId = this.getNextNodeId(node.id, edges);
        break;

      default:
        nextNodeId = this.getNextNodeId(node.id, edges);
    }

    if (nextNodeId) {
      const nextNode = nodes.find(n => n.id === nextNodeId);
      if (nextNode) {
        await this.executeNode(nextNode, nodes, edges, context);
      }
    }
  }

  private async executeActionNode(data: any, context: ExecutionContext): Promise<void> {
    const { actionType, config } = data;

    switch (actionType) {
      case 'send_message':
        if (context.userId && config.message) {
          const message = this.replaceVariables(config.message, context.variables);
          await whatsappService.sendMessage({
            to: context.userId,
            message: message
          });
        }
        break;

      case 'wait':
        const waitMs = (config.waitTime || 1) * (config.waitUnit === 'minutes' ? 60000 : 1000);
        await new Promise(resolve => setTimeout(resolve, Math.min(waitMs, 5000))); // Cap at 5s for testing
        break;
    }
  }

  private executeConditionNode(
    node: Node,
    data: any,
    edges: Edge[],
    context: ExecutionContext
  ): string | null {
    const { conditionType, config } = data;
    let result = false;

    switch (conditionType) {
      case 'contains':
        result = context.message?.toLowerCase().includes(config.value?.toLowerCase() || '') || false;
        break;

      case 'equals':
        result = context.message === config.value;
        break;

      case 'starts_with':
        result = context.message?.startsWith(config.value || '') || false;
        break;

      case 'time_based':
        const hour = new Date().getHours();
        const start = parseInt(config.timeStart?.split(':')[0] || '0');
        const end = parseInt(config.timeEnd?.split(':')[0] || '23');
        result = hour >= start && hour <= end;
        break;
    }

    const handleId = result ? 'true' : 'false';
    const edge = edges.find(e => e.source === node.id && e.sourceHandle === handleId);
    return edge?.target || null;
  }

  private async executeDelayNode(data: any): Promise<void> {
    const { delayType, config } = data;
    let delayMs = 0;

    if (delayType === 'fixed') {
      const unit = config.delayUnit === 'minutes' ? 60000 : config.delayUnit === 'hours' ? 3600000 : 1000;
      delayMs = (config.delayTime || 0) * unit;
    } else if (delayType === 'random') {
      const min = (config.minDelay || 0) * 1000;
      const max = (config.maxDelay || 60) * 1000;
      delayMs = Math.random() * (max - min) + min;
    }

    // Cap delay at 5 seconds for testing
    if (delayMs > 0) {
      await new Promise(resolve => setTimeout(resolve, Math.min(delayMs, 5000)));
    }
  }

  private getNextNodeId(currentNodeId: string, edges: Edge[]): string | null {
    const edge = edges.find(e => e.source === currentNodeId);
    return edge?.target || null;
  }

  private replaceVariables(text: string, variables: Record<string, any>): string {
    return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      return variables[key] || match;
    });
  }

  /**
   * Check if any flows should be triggered by a message
   * Note: This is now handled by the backend
   */
  async checkTriggers(message: string, userId: string): Promise<void> {
    console.log('Trigger check delegated to backend');
    // Backend handles all trigger checking now
  }
}

export default new FlowExecutor();
